import React, { useMemo } from "react";

import type { MeetingStatus } from "@performyard/models";
import { Chip, Text } from "@performyard/py-components";

import AvatarGroup from "../../../../components/AvatarGroup";
import ExpandableText from "./ExpandableText";
import MeetingCalendarIcon from "./MeetingCalendarIcon";
import MeetingOptionsDropdown from "./MeetingOptionsDropdown";

import { formatMeetingDateTimeDisplay } from "../utils";

import { MEETING_STATUS_CONFIG, config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Header section displaying meeting details: name, participants, status, and options.
 */
const MeetingDetailsHeader = (): React.ReactElement => {
	const { meeting, userId, setEditingMeeting, isAdminView, workspace } = useMeetingContext();

	const hasDescription = Boolean(meeting.description?.trim());
	const participantNameById = useMemo(() => {
		return new Map(
			(workspace?.participants || []).map((participant) => [participant.userId, participant.name || ""])
		);
	}, [workspace?.participants]);

	const getStatusChip = (status: MeetingStatus): React.ReactElement => {
		const { label, color } = MEETING_STATUS_CONFIG[status];
		return <Chip label={label} color={color} size={24} />;
	};

	const renderMeetingDateTime = (): React.ReactElement => {
		const { dateStr, timeStr, isMultiDay } = formatMeetingDateTimeDisplay(
			meeting.startDateTime,
			meeting.endDateTime
		);

		if (isMultiDay) {
			return (
				<Text size="paragraph-3" weight="regular">
					{dateStr}
				</Text>
			);
		}

		return (
			<Text size="paragraph-3" weight="regular">
				{dateStr} • {timeStr}
			</Text>
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<Text size="heading-5" weight="semi-bold" dangerouslySetClassName="py-line-clamp-1">
						{meeting.name}
					</Text>
					<AvatarGroup
						employees={meeting.participants.map((participant) => {
							return {
								_id: participant.userId,
								name: participantNameById.get(participant.userId) || "",
							};
						})}
						maxLength={2}
						size={config.layout.smallAvatarSize}
						employeeNameTooltip={true}
					/>
					{meeting.status !== "active" && (
						<div className="flex-shrink-0">{getStatusChip(meeting.status)}</div>
					)}
					{!isAdminView && (
						<div className={`${config.layoutClasses.optionsDropdown} ml-auto`}>
							<MeetingOptionsDropdown
								meeting={meeting}
								userId={userId}
								setEditingMeeting={setEditingMeeting}
							/>
						</div>
					)}
				</div>
				<div className="flex items-center gap-1">
					<MeetingCalendarIcon meeting={meeting} />
					{renderMeetingDateTime()}
				</div>
			</div>

			{hasDescription && (
				<ExpandableText content={meeting.description || ""} collapseHeight={config.descriptionCollapseHeight} />
			)}
		</div>
	);
};

export default MeetingDetailsHeader;
